import React, { useState, useMemo, useEffect, useRef } from "react";
import { useAllPension } from '../../hooks/products/pension/useAllPension';
import { useSchemeBeneficiaries } from '../../hooks/products/pension/useSchemeBeneficiaries';
import { usePensionContributions } from '../../hooks/products/pension/usePensionContributions';
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { Spin } from 'antd';

function PensionPage() {
  // Fetch all pension data and log the structure
  const { data: allPensionData, isLoading: isLoadingPension, isFetching: isFetchingPension } = useAllPension();
  useEffect(() => {
    if (allPensionData) {
      console.log('Fetched all pension data:', allPensionData);
    }
  }, [allPensionData]);
console.log("pesin", allPensionData);
  // Scheme selection and beneficiaries
  const [selectedSchemePayload, setSelectedSchemePayload] = useState(null);
  const [selectedSchemeKey, setSelectedSchemeKey] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    data: schemeBeneficiariesData,
    isFetching: isFetchingBeneficiaries,
    isLoading: isLoadingBeneficiaries,
    error: schemeBeneficiariesError,
  } = useSchemeBeneficiaries(selectedSchemePayload);

  // Fetch contributions data when a scheme is selected
  const [fetchContributions, setFetchContributions] = useState(false);
  const [contributionsMemberNumber, setContributionsMemberNumber] = useState(null);
  const {
    data: contributionsData,
    isLoading: isLoadingContributions,
  } = usePensionContributions(contributionsMemberNumber, fetchContributions);

  useEffect(() => {
    if (schemeBeneficiariesData) {
      console.log('Fetched scheme beneficiaries:', schemeBeneficiariesData);
    }
  }, [schemeBeneficiariesData]);

  useEffect(() => {
    if (contributionsData) {
      console.log('Fetched pension contributions:', contributionsData);
    }
  }, [contributionsData]);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = useNavigate();

  const buildProductDetailsPayload = (product, contributionsSummary) => ({
    policyName: product.name,
    policyNumber: product.policyNumber,
    status: product.status,
    memberName: product.memberName,
    penTypeDescription: product.penTypeDescription,
    employerName: product.employerName,
    employerNumber: product.employerNumber,
    ssnitNumber: product.ssnitNumber,
    totalPremiumPaid: product.totalPremiumPaid,
    modalPremium: product.modalPremium,
    premiumAmount: product.premiumAmount,
    premiumFrequency: product.premiumFrequency,
    // Use contributions data if available, fallback to product data
    totalRedemption: contributionsSummary?.totalRedemption ?? product.totalRedemption ?? 0,
    totalContributions: contributionsSummary?.totalContributions ?? product.totalContributions ?? 0,
    accruedInterest: contributionsSummary?.accruedInterest ?? product.accruedInterest ?? 0,
    accountValue: contributionsSummary?.accountValue ?? product.schemeCurrentValue ?? 0,
    schemeCurrentValue: product.schemeCurrentValue,
    balanceBroughtForward: product.balanceBroughtForward,
    startDate: product.startDate,
    endDate: product.endDate,
  });

  // Tabs based on common pension types from API
  // Tabs for pension status categories (based on API Status)
  const productTabs = [
    { key: 'all', label: 'All', tooltip: 'Show all pension schemes regardless of status.' },
    { key: 'active', label: 'Active', tooltip: 'Schemes with active status.' },
    { key: 'dormant', label: 'Dormant', tooltip: 'Schemes that are dormant.' },
    { key: 'inactive', label: 'Inactive', tooltip: 'Schemes that are inactive.' },
  ];

  const [activeTab, setActiveTab] = useState('all');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, text: '', show: false });
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Helper function to select a scheme and get its beneficiaries
  const handleSchemeSelection = (schemeData) => {
    const schemeKey = getSchemeIdentifier(schemeData) || schemeData.MemberNumber || schemeData.memberNumber || null;
    if (!schemeKey) {
      throw new Error('Unable to determine scheme identifier.');
    }

    const dateJoinedValue =
      schemeData.Date_joined ||
      schemeData.date_joined ||
      schemeData.DateJoined ||
      schemeData.dateJoined ||
      schemeData.EffectiveDate ||
      schemeData.startDate ||
      schemeData.StartDate ||
      '';

    const normalizedPayload = {
      memberName: schemeData.MemberName || schemeData.memberName || '',
      employerNumber: schemeData.EmployerNumber || schemeData.employerNumber || '',
      employerName: schemeData.EmployerName || schemeData.employerName || '',
      ssnitNumber: schemeData.SSNITNumber || schemeData.ssnitNumber || '',
      memberNumber: schemeData.MemberNumber || schemeData.memberNumber || '',
      masterScheme: schemeData.MasterSchemeDescription || schemeData.masterScheme || '',
      schemeType: schemeData.PenTypeDescription || schemeData.schemeType || '',
      email: schemeData.Email || schemeData.email || '',
      dob: schemeData.Dob || schemeData.dob || '',
      dateJoined: dateJoinedValue,
      sex: schemeData.Sex || schemeData.sex || '',
      nationality: schemeData.Nationality || schemeData.nationality || '',
      requestId: `${schemeKey}-${Date.now()}`,
    };

    setSelectedSchemeKey(schemeKey);
    setSelectedSchemePayload(normalizedPayload);
    return schemeKey;
  };

  // Extract beneficiaries from scheme beneficiaries data
  const schemeBeneficiaries = useMemo(() => {
    const rawPayload = schemeBeneficiariesData?.data;
    if (!rawPayload) return [];

    const candidateSchemes = (() => {
      if (Array.isArray(rawPayload)) return rawPayload;
      if (Array.isArray(rawPayload?.data)) return rawPayload.data;
      if (Array.isArray(rawPayload?.beneficiary)) return [rawPayload];
      if (Array.isArray(rawPayload?.data?.beneficiary)) return [rawPayload.data];
      return [];
    })();

    if (!candidateSchemes.length) return [];

    return candidateSchemes.flatMap((scheme) => {
      const beneficiariesForScheme = Array.isArray(scheme?.beneficiary) ? scheme.beneficiary : [];
      if (!beneficiariesForScheme.length) return [];

      const schemeIdFromResponse = getSchemeIdentifier(scheme) || selectedSchemeKey;

      return beneficiariesForScheme.map((b) => {
        const percentageValue = b.perc_alloc ?? b.percentage_allocation ?? b.percentage ?? 0;
        return {
          member_id: schemeIdFromResponse,
          beneficiaryId: b.beneficiary_id ?? b.id ?? null,
          name: b.FullName || b.full_name || b.name || '',
          perc_alloc: percentageValue,
          percAllocValue: Number(percentageValue) || 0,
          relationship: b.Relationship || b.relationship || '',
          birth_date: b.birth_date || b.dob || '',
          address: b.address || '',
        };
      });
    });
  }, [schemeBeneficiariesData, selectedSchemeKey]);

  const allProducts = useMemo(() => {
    return Array.isArray(allPensionData?.data)
          ? allPensionData.data.map((p, idx) => {
          // Normalize the status coming from API (e.g., 'ACTIVE' -> 'active')
          const normalizedStatus = String(p.Status || p.status || '')
            .toLowerCase()
            .trim();
          // For now, we'll show schemes without beneficiaries initially
          // Beneficiaries will be loaded when a scheme is selected
          const matchingBeneficiaries = schemeBeneficiaries
            .filter((b) => {
              if (b.member_id) {
                return b.member_id === p.MemberId;
              }
              return selectedSchemeKey === p.MemberId;
            })
            .map((b) => ({
              beneficiaryId: b.beneficiaryId ?? b.beneficiary_id ?? null,
              name: b.name || '',
              percentage: typeof b.perc_alloc === 'number' ? `${b.perc_alloc}%` : b.perc_alloc,
              percAllocValue: b.percAllocValue ?? (Number(b.perc_alloc) || 0),
              relationship: b.relationship || '',
              dob: b.birth_date || '',
              address: b.address || '',
              memberId: b.member_id,
            }));
          
          return {
            id: p.MemberId || `pension${idx+1}`,
            type: 'pension',
            name: p.MasterSchemeDescription || '',
            number: p.MemberNumber || '',
            // Use normalizedStatus for categorization (active/dormant/inactive)
            status: normalizedStatus || (p.PenTypeDescription || '').toLowerCase().replace(/[^a-z0-9]/g, '_'),
            policyNumber: p.MemberNumber || '',
            memberName: p.MemberName || '',
            penTypeDescription: p.PenTypeDescription || '',
            premiumAmount: p.MonthlyContribution || 0,
            premiumFrequency: 'Monthly',
            totalPremiumPaid: p.SchemeCurrentValue || 0,
            modalPremium: p.MonthlyContribution || 0,
            totalRedemption: 0, // Not available in pension data - from /redemptions endpoint
            totalContributions: p.TotalContributions || p.totalContributions || 0, // Not directly available from API
            accruedInterest: p.AccruedInterest || p.accruedInterest || 0, // Would need calculation: CurrentValue - (Contributions - Redemptions)
            schemeCurrentValue: p.SchemeCurrentValue || p.schemeCurrentValue || 0,
            balanceBroughtForward: p.BalanceBroughtForward || p.balanceBroughtForward || 0,
            startDate: p.EffectiveDate || '',
            endDate: '', // Not available in pension data
            ssnitNumber: p.ssnitNumber || p.SSNITNumber || '',
            employerNumber: p.EmployerNumber || '',
            employerName: p.EmployerName || '',
            email: p.Email || '',
            dob: p.Dob || '',
            dateJoined: p.Date_joined || '',
            sex: p.Sex || '',
            nationality: p.Nationality || '',
            beneficiaries: matchingBeneficiaries.map(b => ({
              beneficiaryId: b.beneficiaryId ?? null,
              name: b.name || '',
              percentage: b.perc_alloc !== undefined ? `${b.perc_alloc}%` : '',
              percAllocValue: typeof b.percAllocValue === 'number'
                ? b.percAllocValue
                : Number(String(b.perc_alloc || '').replace(/[^0-9.]/g, '')) || 0,
              relationship: b.relationship || '',
              dob: b.birth_date || '',
              address: b.address || '',
            })),
            // Add raw scheme data for selection
            rawSchemeData: p
          };
        })
      : [];
  }, [allPensionData?.data, selectedSchemeKey, schemeBeneficiaries]);

  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      console.log('Mapped pension products:', allProducts);
    }
  }, [allProducts]);

  // Helper function to handle product navigation with scheme selection
  const handleProductClick = (product) => {
    try {
      setLoading(true);
      setPendingProduct(product);
      // Don't fetch contributions yet - wait for scheme selection to complete
      setFetchContributions(false);
      handleSchemeSelection(product.rawSchemeData);
    } catch (error) {
      console.error('Failed to load scheme details:', error);
      setPendingProduct(null);
      setSelectedSchemePayload(null);
      setSelectedSchemeKey(null);
      setFetchContributions(false);
      // Navigate anyway with current data
      setLoading(true);
      navigate('/dashboard/pensions-product-details', { 
        state: { 
          productDetails: buildProductDetailsPayload(product),
          beneficiaries: product.beneficiaries, 
          category: 'pension' 
        } 
      });
    }
  };

  useEffect(() => {
    if (!pendingProduct) return;

    const targetSchemeId = getSchemeIdentifier(pendingProduct.rawSchemeData) || pendingProduct.id;
    if (!targetSchemeId) return;
    if (selectedSchemeKey !== targetSchemeId) return;
    if (isLoadingBeneficiaries || isFetchingBeneficiaries) return;
    if (!schemeBeneficiariesData && !schemeBeneficiariesError) return;
    
    // Now that scheme is selected, trigger contributions fetch with member number
    if (!fetchContributions) {
      setContributionsMemberNumber(pendingProduct.policyNumber);
      setFetchContributions(true);
      return; // Wait for next effect run after contributions load
    }
    // Wait for contributions data to finish loading
    if (isLoadingContributions) return;

    const beneficiariesForScheme = schemeBeneficiaries
      .filter((b) => {
        if (b.member_id) return b.member_id === targetSchemeId;
        return selectedSchemeKey === targetSchemeId;
      })
      .map((b) => ({
        beneficiaryId: b.beneficiaryId ?? null,
        name: b.name || '',
        percentage: b.perc_alloc !== undefined ? `${b.perc_alloc}%` : '',
        percAllocValue: typeof b.percAllocValue === 'number'
          ? b.percAllocValue
          : Number(String(b.perc_alloc || '').replace(/[^0-9.]/g, '')) || 0,
        relationship: b.relationship || '',
        dob: b.birth_date || '',
        address: b.address || '',
      }));

    // Build payload with contributions summary if available
    const contributionsSummary = contributionsData?.summary;

    setLoading(true);
    navigate('/dashboard/pensions-product-details', {
      state: {
        productDetails: buildProductDetailsPayload(pendingProduct, contributionsSummary),
        beneficiaries: beneficiariesForScheme,
        category: 'pension',
        contributionsData: contributionsData || null, // Pass full contributions data
      },
    });

    setPendingProduct(null);
    setFetchContributions(false); // Reset flag
    setContributionsMemberNumber(null); // Reset member number
  }, [
    pendingProduct,
    selectedSchemeKey,
    isLoadingBeneficiaries,
    isFetchingBeneficiaries,
    schemeBeneficiariesData,
    schemeBeneficiariesError,
    schemeBeneficiaries,
    fetchContributions,
    contributionsMemberNumber,
    isLoadingContributions,
    contributionsData,
    navigate,
  ]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const el = tabsContainerRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  const scrollTabs = (direction) => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const amount = direction === 'left' ? -220 : 220;
    container.scrollBy({ left: amount, behavior: 'smooth' });
  };

  // Filter products by selected status tab
  const filteredProducts = activeTab === 'all'
    ? allProducts
    : allProducts.filter(p => {
        const s = String(p.status || '').toLowerCase();
        return s === activeTab;
      });

  // Update scroll buttons when tab or products change
  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    });
  }, [activeTab, filteredProducts.length]);

  return (
    <div className="life-page-container" style={{
      padding: isMobile ? 8 : 16,
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingTop: 16, width: '100%' }}>
        <div className="life-page-card" style={{
          background: '#fff',
          borderRadius: isMobile ? 16 : 28,
          boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)',
          padding: isMobile ? '16px 12px' : '32px 40px',
          minWidth: isMobile ? 280 : 320,
          maxWidth: 900,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 12
        }}>
          <h2 className="page-title" style={{ fontWeight: 700, color: '#222', marginBottom: 0, textAlign: 'left' }}>Pensions</h2>
          
          {/* Product Tabs */}
          <div
            className="tab-scroll-wrapper"
            style={{
              position: 'relative',
              width: '100%',
              marginBottom: 0,
              paddingTop: '20px',
              paddingBottom: '8px'
            }}
          >
            <button
              type="button"
              className="tab-scroll-btn"
              aria-label="Scroll tabs left"
              onClick={() => scrollTabs('left')}
              disabled={!canScrollLeft}
              style={{ left: 12, opacity: canScrollLeft ? 1 : 0 }}
            >
              <ChevronLeft size={18} />
            </button>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              width: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: '0 8px'
            }}
              className="tab-scrollbar-hide"
              ref={tabsContainerRef}
            >
            {productTabs.map(tab => (
              <div 
                key={tab.key} 
                className="tab-tooltip-wrapper" 
                style={{ position: 'relative' }}
                onMouseEnter={e => {
                  if (tab.tooltip) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({
                      x: rect.left + rect.width / 2,
                      y: rect.bottom + 12, // 12px below the tab
                      text: tab.tooltip,
                      show: true
                    });
                  }
                }}
                onMouseLeave={() => {
                  setTooltipPosition(pos => ({ ...pos, show: false }));
                }}
              >
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className="tab-button"
                  style={{
                    border: activeTab === tab.key ? 'none' : '2px solid #222',
                    background: activeTab === tab.key ? 'linear-gradient(90deg, #e6f9ed 0%, #b6f7d2 100%)' : '#fff',
                    color: activeTab === tab.key ? '#222' : '#222',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 16,
                    padding: '8px 22px',
                    outline: 'none',
                    boxShadow: activeTab === tab.key ? '0 2px 8px 0 rgba(0,100,0,0.08)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    borderWidth: activeTab === tab.key ? 0 : 2,
                    whiteSpace: 'nowrap',
                    marginBottom: 2,
                    marginRight: 12,
                  }}
                  tabIndex={0}
                  aria-pressed={activeTab === tab.key}
                  onFocus={() => {}}
                  onBlur={() => {}}
                >
                  {tab.label}
                </button>
                {/* Tooltip is now rendered via portal below */}
      {/* Tooltip Portal: Rendered outside tab section for visibility */}
      {tooltipPosition.show && tooltipPosition.text && createPortal(
        <div
          className="tab-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translate(-50%, 0)',
            background: 'linear-gradient(90deg, #e0faeaff 0%, #ffffffff 70%)',
            color: '#000000ff',
            padding: '18px 28px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 700,
            whiteSpace: 'normal',
            maxWidth: '320px',
            textAlign: 'center',
            zIndex: 2147483647,
            boxShadow: '0 6px 5px rgba(0, 0, 0, 0.03)',
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.2s ease',
            lineHeight: '1.4'
          }}
        >
          {tooltipPosition.text}
          <div style={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid #b6f7d2'
          }} />
        </div>,
        document.body
      )}
              </div>
            ))}
            </div>
            <button
              type="button"
              className="tab-scroll-btn"
              aria-label="Scroll tabs right"
              onClick={() => scrollTabs('right')}
              disabled={!canScrollRight}
              style={{ right: 12, opacity: canScrollRight ? 1 : 0 }}
            >
              <ChevronRight size={18} />
            </button>
            <div className="tab-scroll-fade tab-scroll-fade-left" style={{ opacity: canScrollLeft ? 1 : 0 }} />
            <div className="tab-scroll-fade tab-scroll-fade-right" style={{ opacity: canScrollRight ? 1 : 0 }} />
          </div>

          {/* Products List */}
          <div style={{ width: '100%' }}>
            {(isLoadingPension || isFetchingPension) ? (
              <div style={{ 
                textAlign: 'center',
                padding: '60px 20px',
              }}>
                <Spin size="large" tip="Loading pension schemes..." />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state-message" style={{ 
                color: '#888', 
                margin: '12px 0 24px 0',
                textAlign: 'center',
                padding: '40px 20px',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '2px dashed #ddd'
              }}>
                No products found for the selected category.
              </div>
            ) : (
              <>
                <div style={{ 
                  color: '#666', 
                  fontSize: 14, 
                  marginBottom: 0,
                  fontWeight: 500,
                  paddingLeft: 4
                }}>
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                </div>
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="insure-row-hover product-row"
                    tabIndex={0}
                    role="button"
                    aria-label={`Go to ${product.name}`}
                    onClick={() => handleProductClick(product)}
                    onKeyDown={e => { 
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleProductClick(product);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                      <span className="product-number">
                        {product.number}
                      </span>
                      <span className="product-name">
                        {product.name}
                      </span>
                    </div>
                    <ChevronRight style={{ fontSize: 26, color: '#bbb', marginLeft: 24 }} size={26} />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Enhanced Styles */}
        <style>{`
          .tab-scrollbar-hide::-webkit-scrollbar { 
            display: none; 
          }
          .tab-scrollbar-hide { 
            -ms-overflow-style: none; 
            scrollbar-width: none; 
            flex-direction: row !important;
            flex-wrap: nowrap !important;
          }
          
          .tab-scroll-wrapper {
            position: relative;
          }
          .tab-scroll-btn {
            position: absolute;
            top: calc(80px - 20px);
            transform: translateY(-50%);
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #00b050;
            box-shadow: 0 10px 25px rgba(0, 176, 80, 0.35);
            cursor: pointer;
            transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
            z-index: 30;
            color: #fff;
          }
          .tab-scroll-btn:disabled {
            pointer-events: none;
            opacity: 0;
          }
          .tab-scroll-btn:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.08);
            box-shadow: 0 12px 28px rgba(0, 176, 80, 0.45);
          }
          @media (max-width: 768px) {
            .tab-scroll-btn {
              display: none !important;
            }
            .tab-scroll-wrapper {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
          }
          .tab-scroll-fade {
            position: absolute;
            top: 70px;
            bottom: 30px;
            width: 36px;
            pointer-events: none;
            transition: opacity 0.2s;
            z-index: 25;
          }
          .tab-scroll-fade-left {
            left: 0;
            background: linear-gradient(90deg, #fff 45%, rgba(255,255,255,0));
          }
          .tab-scroll-fade-right {
            right: 0;
            background: linear-gradient(270deg, #fff 45%, rgba(255,255,255,0));
          }
          
          .tab-tooltip-wrapper { 
            position: relative; 
            display: inline-flex; 
            vertical-align: top; 
            z-index: 20; 
          }
          
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          .insure-row-hover:hover, .insure-row-hover:focus {
            background: linear-gradient(90deg, #e6f9ed 0%, #d2f7e3 100%) !important;
            border-color: #b6f7d2 !important;
            outline: none !important;
          }
          .insure-row-hover:focus {
            box-shadow: 0 0 0 2px rgba(182, 247, 210, 0.5) !important;
          }
          /* Divider and responsive typography for pension list */
          .product-row {
            display: flex;
            align-items: center;
            width: 100%;
            min-height: 70px;
            border-radius: 14px;
            padding: 12px 18px;
            margin-bottom: 0;
            cursor: pointer;
            transition: background 0.18s;
            border: 1px solid transparent;
            border-bottom: 1px solid #eee;
            background-clip: padding-box;
          }
          .product-row:last-child { border-bottom: none; }

          .product-number { color: #888; font-weight: 500; font-size: 15px; letter-spacing: 1; margin-bottom: 2px; line-height: 1.1; }
          .product-name { font-weight: 500; font-size: 21px; color: #111; line-height: 1.2; }

          /* Responsive adjustments */
          @media (max-width: 900px) {
            .product-row { min-height: 64px; padding: 10px 14px; }
            .product-name { font-size: 18px !important; }
            .product-number { font-size: 13px !important; }
          }

          /* Mobile responsive padding for main card */
          @media (max-width: 768px) {
            .life-page-card {
              padding: 32px 24px !important;
            }
            .life-page-container {
              padding: 12px !important;
            }
          }
          
          @media (max-width: 520px) {
            .life-page-card {
              padding: 24px 16px !important;
            }
            .life-page-container {
              padding: 8px !important;
            }
          }
          
          @media (max-width: 600px) {
            .product-row { min-height: 60px; padding: 8px 12px; }
            .product-name { font-size: 12px !important; }
            .product-number { font-size: 9px !important; }
            .life-page-container {
              padding: 4px !important;
              padding-top: 8px !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            .life-page-card {
              padding: 12px 8px !important;
              border-radius: 12px !important;
              max-width: none !important;
              min-width: auto !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            .tab-button {
              margin-right: 6px !important;
              padding: 6px 12px !important;
              font-size: 14px !important;
            }
          }
          
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          .insure-row-hover:hover, .insure-row-hover:focus {
            background: linear-gradient(90deg, #e6f9ed 0%, #d2f7e3 100%) !important;
            border-color: #b6f7d2 !important;
            outline: none !important;
          }
          .insure-row-hover:focus {
            box-shadow: 0 0 0 2px rgba(182, 247, 210, 0.5) !important;
          }
        `}</style>
        
        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <Spin size="large" tip="Loading..." />
          </div>
        )}
      </div>
    </div>
  );
}

export default PensionPage;

function getSchemeIdentifier(schemeLike) {
  if (!schemeLike) return null;
  return (
    schemeLike.MemberId ??
    schemeLike.member_id ??
    schemeLike.memberId ??
    schemeLike.memberID ??
    schemeLike.member_number ??
    schemeLike.MemberNumber ??
    schemeLike.memberNumber ??
    schemeLike.scheme_id ??
    schemeLike.schemeId ??
    schemeLike.id ??
    null
  );
}